import React from 'react';
import BeaconProChart, {
  DefaultMenuButton
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';

import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useContentChartSubtitles } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useDynamicSubtitle';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import {
  KeywordFilter,
  GaugeData
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import { KeywordsPerformanceSummaryAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/KeywordsPerformanceSummaryAdapter';
import {
  CardContainer,
  CardDetails,
  GaugeSummaryCard,
  GaugeSummaryCardSkeleton
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/GaugeSummaryCard';
import { useKeywordSubtitleV3 } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useKeywordSubtitleV3';
import { SummaryCardTilesHeaders } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentKeywordUtils';

const usageOrderMap = {
  [KeywordFilter.Usage]: 0,
  [KeywordFilter.KeywordBenchmark]: 1,
  [KeywordFilter.LowUsage]: 2
};

const productivityOrderMap = {
  [KeywordFilter.Productivity]: 0,
  [KeywordFilter.UnProductiveKeywords]: 1,
  [KeywordFilter.LowProductivity]: 2
};

const popularityOrderMap = {
  [KeywordFilter.Popularity]: 0,
  [KeywordFilter.PopularityBenchmark]: 1,
  [KeywordFilter.LowPopularity]: 2
};

interface OrderMap {
  [key: string]: number;
}

const getGroupedDataItem = (title: string, data: GaugeData[], orderMap: OrderMap) => {
  const filterKeys = Object.keys(orderMap) as KeywordFilter[];
  return {
    title,
    data: data
      .filter((item) => filterKeys.includes(item.filterKey))
      .sort((a, b) => orderMap[a.filterKey] - orderMap[b.filterKey])
  };
};

interface KeywordsGaugeCardsProps {
  title: string;
  predefinedColor?: string;
  queryFn: () => Promise<any>;
  renderSubtitle: () => string | React.ReactNode;
}

export const KeywordsGaugeCards = (props: KeywordsGaugeCardsProps) => {
  const { title, predefinedColor, queryFn, renderSubtitle } = props;
  const { generateKeywordPerformanceSubtitle } = useContentChartSubtitles();
  const { generateMetricSubtitle } = useKeywordSubtitleV3();
  const entityType = useMainEntityType();

  return (
    <BeaconProChart queryFn={queryFn} name={`keywords-gauge-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <BeaconProChart.Header menuButton={() => <DefaultMenuButton excludeActions={['exportCsv']} />}>
        <BeaconProChart.Title>
          <Text variant="h4">{title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle render={renderSubtitle ?? (() => generateKeywordPerformanceSubtitle())} />

      <BeaconProChart.Visualization
        render={(adapter: KeywordsPerformanceSummaryAdapter, isLoading: boolean) => {
          if (isLoading) {
            return (
              <Flex marginTop="32px" gap="md">
                {[...Array(3)].map((_, index) => (
                  <GaugeSummaryCardSkeleton key={index} />
                ))}
              </Flex>
            );
          }

          const fullData = adapter.transform(entityType === 'product');
          // we don't want to show low usage, low productivity, or low popularity keywords for product level
          if (entityType === 'product') {
            fullData.gaugeData = fullData.gaugeData.filter(
              (item) =>
                ![KeywordFilter.LowUsage, KeywordFilter.LowProductivity, KeywordFilter.LowPopularity].includes(
                  item.filterKey
                )
            );
          }

          const groupedData = [
            getGroupedDataItem('Keyword Usage', fullData.gaugeData, usageOrderMap),
            getGroupedDataItem('Keyword Productivity', fullData.gaugeData, productivityOrderMap),
            getGroupedDataItem('Keyword Popularity', fullData.gaugeData, popularityOrderMap)
          ];

          return (
            <Flex marginTop="32px" gap="md">
              {groupedData.map((group) => {
                const tileData = group.data;
                return (
                  <CardContainer key={group.title}>
                    <GaugeSummaryCard
                      key={group.title}
                      title={group.title}
                      predefinedColor={predefinedColor}
                      percentage={tileData[0].percentage}
                    />
                    {tileData.map((item, index) => {
                      return (
                        <CardDetails
                          key={item.filterKey}
                          title={
                            SummaryCardTilesHeaders[item.filterKey]?.[entityType]?.toUpperCase() ??
                            SummaryCardTilesHeaders[item.filterKey]?.default?.toUpperCase()
                          }
                          subtitle={generateMetricSubtitle(fullData, item.filterKey)}
                          item={item}
                          overrideSx={
                            index === tileData.length - 1
                              ? {
                                  borderBottomLeftRadius: '8px',
                                  borderBottomRightRadius: '8px'
                                }
                              : undefined
                          }
                        />
                      );
                    })}
                  </CardContainer>
                );
              })}
            </Flex>
          );
        }}
      />
    </BeaconProChart>
  );
};
